import type { PriceQuote } from "@kitchen/schemas";
import {
  Button,
  Chip,
  faDigits,
  Field,
  formatDateShort,
  formatMoney,
  Panel,
  parseMoney,
  Seg,
} from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";
import { type RuleRow, useItems, useOrgs, useTiers } from "./data.js";

type Level = RuleRow["level"];
const LEVELS: { value: Level; label: string; hint: string }[] = [
  {
    value: "organization-default",
    label: "قیمت پرس سازمان",
    hint: "یک قیمت برای هر سطح، هر غذایی باشد (الگوی اکسل)",
  },
  { value: "organization-item", label: "استثنای غذا برای سازمان", hint: "مثلاً ماهی برای شهرداری گران‌تر" },
  { value: "unit-item", label: "استثنای غذا برای یک واحد", hint: "قیمت توافقی یک واحد" },
  { value: "item-base", label: "قیمت پایه‌ی غذا", hint: "وقتی هیچ قیمت دیگری نیست" },
];
const LEVEL_LABEL = Object.fromEntries(LEVELS.map((l) => [l.value, l.label])) as Record<Level, string>;

/**
 * F3-T12: price rules (architecture §7.3, DL-031/033/034). Dated, specific-first; a correction to the past is
 * allowed and audited. Amounts are typed and shown in toman, stored as integer rial (AS-030).
 */
export function PricesPage() {
  const { orgs, units } = useOrgs();
  const items = useItems();
  const tiers = useTiers();
  const [orgFilter, setOrgFilter] = useState("");
  const rules = useQuery({
    queryKey: ["price-rules", orgFilter],
    queryFn: () => api<RuleRow[]>(`/prices/rules${orgFilter ? `?organizationId=${orgFilter}` : ""}`),
  });
  const itemName = (id: string | null) => (id ? (items.data?.find((i) => i.id === id)?.name ?? "—") : "");
  const tierName = (id: string | null) =>
    id ? (tiers.data?.find((t) => t.id === id)?.name ?? "—") : "بدون سطح";
  const orgName = (id: string | null) => faDigits(orgs.find((o) => o.id === id)?.name ?? "");
  const unitName = (id: string | null) => faDigits(units.find((u) => u.id === id)?.name ?? "");
  const target = (r: RuleRow) =>
    r.level === "unit-item"
      ? `${unitName(r.unitId)} · ${itemName(r.menuItemId)}`
      : r.level === "organization-item"
        ? `${orgName(r.organizationId)} · ${itemName(r.menuItemId)}`
        : r.level === "organization-default"
          ? orgName(r.organizationId)
          : itemName(r.menuItemId);

  const refresh = [["price-rules"], ["price-quote"]];
  const edit = useAction(async (r: RuleRow) => {
    const typed = window.prompt(
      `قیمت تازه (تومان) برای «${target(r)} — ${tierName(r.tierId)}»:`,
      String(r.amount / 10),
    );
    if (typed === null) return;
    const amount = parseMoney(typed);
    if (amount === null) throw new Error("مبلغ باید عدد صحیح باشد");
    const note = window.prompt("یادداشت (مثلاً «اصلاح نوسان»):", r.note) ?? r.note;
    await api(`/prices/rules/${r.id}`, {
      method: "PUT",
      body: { version: r.version, amount, validFrom: r.validFrom, validTo: r.validTo, note },
    });
  }, refresh);
  const archive = useAction(async (r: RuleRow) => {
    const reason = window.prompt("دلیل حذف این قیمت؟ (بایگانی می‌شود و در ردپا می‌ماند)");
    if (reason?.trim())
      await api(`/prices/rules/${r.id}/archive`, { body: { version: r.version, reason: reason.trim() } });
  }, refresh);

  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: "minmax(0, 1.4fr) minmax(0, 1fr)", alignItems: "start" }}
    >
      <Panel
        title="قیمت‌ها"
        actions={
          <select
            className="input"
            aria-label="فیلتر سازمان"
            value={orgFilter}
            onChange={(e) => {
              setOrgFilter(e.target.value);
            }}
          >
            <option value="">همه‌ی قیمت‌ها</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {"— ".repeat(o.depth)}
                {faDigits(o.name)}
              </option>
            ))}
          </select>
        }
      >
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>نوع</th>
                <th>برای</th>
                <th>سطح</th>
                <th className="num">قیمت</th>
                <th>از</th>
                <th>تا</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(rules.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="t-cap">{LEVEL_LABEL[r.level]}</td>
                  <td>
                    {target(r)}
                    {r.note && <div className="t-cap muted">{r.note}</div>}
                  </td>
                  <td>{tierName(r.tierId)}</td>
                  <td className="num b">{formatMoney(r.amount)}</td>
                  <td className="num">{formatDateShort(r.validFrom)}</td>
                  <td className="num">{r.validTo ? formatDateShort(r.validTo) : "—"}</td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      <Button
                        variant="ghost"
                        icon="edit"
                        onClick={() => {
                          edit.run(r);
                        }}
                        aria-label={`ویرایش قیمت ${target(r)}`}
                      >
                        ویرایش
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          archive.run(r);
                        }}
                        aria-label={`بایگانی قیمت ${target(r)}`}
                      >
                        بایگانی
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rules.data?.length === 0 && <p className="muted">قیمتی ثبت نشده است.</p>}
        <p className="panel-foot t-cap">
          ترتیب: قیمت واحد ← استثنای غذای سازمان ← قیمت پرس سازمان ← قیمت پایه. بین چند قیمت هم‌سطح، تازه‌ترین
          تاریخ شروع برنده است.
        </p>
        <ErrorLine error={edit.error ?? archive.error} />
      </Panel>
      <div className="stack">
        <NewRule />
        <QuoteBox />
      </div>
    </div>
  );
}

function NewRule() {
  const { orgs, units } = useOrgs();
  const items = useItems();
  const tiers = useTiers();
  const [level, setLevel] = useState<Level>("organization-default");
  const [orgId, setOrgId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [itemId, setItemId] = useState("");
  const [tierId, setTierId] = useState("");
  const [amount, setAmount] = useState("");
  const [from, setFrom] = useState(tehranToday);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const item = items.data?.find((i) => i.id === itemId);
  const needsItem = level !== "organization-default";
  const needsTier = !(needsItem && item?.kind === "companion");
  const save = useAction(async () => {
    const rial = parseMoney(amount);
    if (rial === null) throw new Error("مبلغ را به تومان و بدون اعشار بنویسید");
    const t =
      level === "unit-item"
        ? { level, unitId, menuItemId: itemId }
        : level === "organization-item"
          ? { level, organizationId: orgId, menuItemId: itemId }
          : level === "organization-default"
            ? { level, organizationId: orgId }
            : { level, menuItemId: itemId };
    await api("/prices/rules", {
      body: {
        target: t,
        tierId: needsTier ? tierId || null : null,
        amount: rial,
        validFrom: from,
        validTo: to || null,
        note,
      },
    });
    setAmount("");
    setNote("");
  }, [["price-rules"], ["price-quote"]]);

  return (
    <Panel title="قیمت تازه">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.run(undefined);
        }}
      >
        <div className="field">
          <label htmlFor="rule-level">نوع</label>
          <select
            id="rule-level"
            className="input"
            value={level}
            onChange={(e) => {
              setLevel(e.target.value as Level);
            }}
          >
            {LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <span className="hint">{LEVELS.find((l) => l.value === level)?.hint}</span>
        </div>
        {(level === "organization-default" || level === "organization-item") && (
          <div className="field">
            <label htmlFor="rule-org">سازمان</label>
            <select
              id="rule-org"
              className="input"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
              }}
            >
              <option value="">انتخاب کنید…</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {"— ".repeat(o.depth)}
                  {faDigits(o.name)}
                </option>
              ))}
            </select>
          </div>
        )}
        {level === "unit-item" && (
          <div className="field">
            <label htmlFor="rule-unit">واحد</label>
            <select
              id="rule-unit"
              className="input"
              value={unitId}
              onChange={(e) => {
                setUnitId(e.target.value);
              }}
            >
              <option value="">انتخاب کنید…</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>
                  {faDigits(u.name)}
                </option>
              ))}
            </select>
          </div>
        )}
        {needsItem && (
          <div className="field">
            <label htmlFor="rule-item">غذا یا قلم همراه</label>
            <select
              id="rule-item"
              className="input"
              value={itemId}
              onChange={(e) => {
                setItemId(e.target.value);
              }}
            >
              <option value="">انتخاب کنید…</option>
              {(items.data ?? [])
                .filter((i) => i.active)
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                    {i.kind === "companion" ? " (همراه)" : ""}
                  </option>
                ))}
            </select>
          </div>
        )}
        {needsTier && (
          <div className="field">
            <label htmlFor="rule-tier">سطح</label>
            <select
              id="rule-tier"
              className="input"
              value={tierId}
              onChange={(e) => {
                setTierId(e.target.value);
              }}
            >
              <option value="">انتخاب کنید…</option>
              {(tiers.data ?? [])
                .filter((t) => t.active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
          </div>
        )}
        <Field
          id="rule-amount"
          label="قیمت هر پرس (تومان)"
          inputMode="numeric"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
          }}
          hint={parseMoney(amount) !== null ? formatMoney(parseMoney(amount) ?? 0) : undefined}
        />
        <div className="row" style={{ gap: "var(--s2)" }}>
          <Field
            id="rule-from"
            label="از تاریخ"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
            }}
            hint={from ? formatDateShort(from) : undefined}
          />
          <Field
            id="rule-to"
            label="تا تاریخ (اختیاری)"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
            }}
            hint={to ? formatDateShort(to) : undefined}
          />
        </div>
        <Field
          id="rule-note"
          label="یادداشت"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
        <Button type="submit" variant="primary" icon="plus" disabled={save.busy || !amount || !from}>
          ثبت قیمت
        </Button>
        <ErrorLine error={save.error} />
      </form>
    </Panel>
  );
}

function QuoteBox() {
  const { units } = useOrgs();
  const items = useItems();
  const tiers = useTiers();
  const [unitId, setUnitId] = useState("");
  const [itemId, setItemId] = useState("");
  const [tierId, setTierId] = useState("");
  const [date, setDate] = useState(tehranToday);
  const item = items.data?.find((i) => i.id === itemId);
  const ready = unitId && itemId && (item?.kind === "companion" || tierId);
  const quote = useQuery({
    queryKey: ["price-quote", unitId, itemId, tierId, date],
    queryFn: () =>
      api<PriceQuote>(
        `/prices/quote?unitId=${unitId}&menuItemId=${itemId}${item?.kind === "food" ? `&tierId=${tierId}` : ""}&date=${date}`,
      ),
    enabled: Boolean(ready),
  });
  const src = quote.data?.source;
  return (
    <Panel title="قیمت این روز چقدر است؟">
      <div className="stack">
        <select
          className="input"
          aria-label="واحد"
          value={unitId}
          onChange={(e) => {
            setUnitId(e.target.value);
          }}
        >
          <option value="">واحد…</option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {faDigits(u.name)}
            </option>
          ))}
        </select>
        <div className="row" style={{ gap: "var(--s2)" }}>
          <select
            className="input grow"
            aria-label="غذا"
            value={itemId}
            onChange={(e) => {
              setItemId(e.target.value);
            }}
          >
            <option value="">غذا…</option>
            {(items.data ?? [])
              .filter((i) => i.active)
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
          </select>
          {item?.kind !== "companion" && (
            <Seg
              label="سطح"
              value={tierId}
              onChange={setTierId}
              options={(tiers.data ?? [])
                .filter((t) => t.active)
                .map((t) => ({ value: t.id, label: t.name }))}
            />
          )}
        </div>
        <Field
          id="quote-date"
          label="روز"
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
          }}
          hint={formatDateShort(date)}
        />
        {quote.data && (
          <div className="card">
            {quote.data.amount === null ? (
              <Chip tone="warn">قیمتی پیدا نشد؛ سفارش ثبت می‌شود ولی بی‌قیمت می‌ماند</Chip>
            ) : (
              <>
                <div className="t-title">{formatMoney(quote.data.amount)}</div>
                <div className="t-cap muted">
                  از «{src?.kind === "rule" ? LEVEL_LABEL[src.level] : "دستی"}»
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}
